// app-pages/ProfilePage.tsx
"use client";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  VStack,
  Switch,
  useToast,
  useClipboard,
  Input,
  Icon,
} from "@chakra-ui/react";
import { Jumbotron, SimpleTable } from "components";
import { useAppContext } from "hooks";
import { useRouter } from "next/navigation";
import {
  useDeleteAccount,
  useGetAPIKey,
  useGenerateAPIKey,
  useDeleteAPIKey,
  useGetUserTransactions,
  useConsentUpdate,
} from "queries";
import moment from "moment";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { FaRegCopy } from "react-icons/fa";

export const ProfilePage = () => {
  const { profile, logOut } = useAppContext();
  const updateConsentsMutation = useConsentUpdate();
  const [nsfwConsent, setNsfwConsent] = useState(profile?.nsfw_consent || false);
  const router = useRouter();
  const deleteAccount = useDeleteAccount();
  const toast = useToast();
  const { data: APIKeyResponse, isLoading: isAPIKeyResponseLoading } = useGetAPIKey();
  const [transactionsPage, setTransactionsPage] = useState<number>(1);
  const { data: transactionsResponse, isLoading: isTransactionsLoading } = useGetUserTransactions({
    pageSize: 4,
    page: transactionsPage,
  });
  const generateAPIKeyMutation = useGenerateAPIKey();
  const deleteAPIKeyMutation = useDeleteAPIKey();
  const {
    onCopy: onAPIKeyCopy,
    value: APIKey,
    setValue: setAPIKey,
    hasCopied: hasAPIKeyCopied,
  } = useClipboard("");

  useEffect(() => {
    setNsfwConsent(profile?.nsfw_consent || false);
  }, [profile]);

  useEffect(() => {
    if (APIKeyResponse !== undefined && APIKeyResponse.api_key !== null) {
      setAPIKey(APIKeyResponse.api_key);
    }
  }, [APIKeyResponse]);
  const handleGenerateAPIKey = async () => {
    await generateAPIKeyMutation.mutateAsync();
  };
  const handleDeleteAPIKey = async () => {
    await deleteAPIKeyMutation.mutateAsync();
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete your account? You will lose your models and images. This action cannot be undone."
      )
    ) {
      await deleteAccount.mutateAsync();
      logOut();
    }
  };

  const handleConfirmClick = () => {
    updateConsentsMutation.mutate(
      {
        nsfwConsent: nsfwConsent,
        newsletterConsent: true,
        adultConsent: true,
      },
      {
        onSuccess: (response) => {
          toast({
            title: "Success",
            description: (response as any).message,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        },
      }
    );
  };

  const transactionsColumns = [
    {
      title: (
        <Text fontWeight={600} fontSize="13px">
          Credits
        </Text>
      ),
      align: "start",
      format: (transaction: any) => (
        <Flex>
          <Text w="80px">{transaction?.credit_amount ?? 0}</Text>
        </Flex>
      ),
    },
    {
      title: (
        <Text fontWeight={600} fontSize="13px">
          Amount
        </Text>
      ),
      align: "start",
      format: (transaction: any) => (
        <Flex>
          <Text w="80px">{transaction?.payment_amount ?? 0}$</Text>
        </Flex>
      ),
    },
    {
      title: (
        <Text fontWeight={600} fontSize="13px">
          Payment
        </Text>
      ),
      align: "start",
      format: (transaction: any) => (
        <Flex>
          <Text w="80px"> {transaction?.source === "one_time_top_up"
            ? "Paypal"
            : transaction?.source === "stripe_top_up"
            ? "Stripe" 
            : transaction?.source === "kesspay_top_up"
            ? "KessPay"
            : transaction?.source === "qfpay_top_up"
            ? "QFPay"
            : "Crypto"}</Text>
        </Flex>
      ),
    },
    {
      title: (
        <Text fontWeight={600} fontSize="13px">
          Date
        </Text>
      ),
      align: "start",
      format: (transaction: any) => (
        <Flex>
          <Text>{moment(new Date(transaction?.created_at)).format("MM/DD/YYYY, HH:mm:ss")}</Text>
        </Flex>
      ),
    },
  ];

  return (
    <Flex direction="column">
      <Jumbotron height="100%">
        <Flex textAlign="center" h="300px" direction="column" justify="center" align="center">
          <Text
            style={{
              background: "linear-gradient(to right,  white, 70%, #A4DBEE)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              MozBackgroundClip: "text",
            }}
            fontSize="56px"
            fontWeight="extrabold"
          >
            Profile
          </Text>
          <Text>Your DeepMode Profile</Text>
        </Flex>
      </Jumbotron>
      <Flex direction="column" align="center">
        <Flex
          mt="39px"
          border="2px solid rgba(255, 255,255,0.2)"
          borderRadius="20px"
          direction="column"
          p="30px"
          w={{ base: "350px", md: "500px" }}
        >
          <Text fontWeight="bold" fontSize="24px">
            Account
          </Text>
          <Text mt="15px">
            <span style={{ fontWeight: 700 }}>Email:</span> {profile?.email}
          </Text>
        </Flex>

        <Flex
          mt="39px"
          border="2px solid rgba(255, 255,255,0.2)"
          borderRadius="20px"
          direction="column"
          p="30px"
          w={{ base: "350px", md: "500px" }}
        >
          <Text fontWeight="bold" fontSize="24px">
            Credits
          </Text>
          <Text mt="15px">Remaining credits: {profile?.credit}</Text>
          <Button
            mt="25px"
            w="250px"
            onClick={() => router.push("/payments")}
            color="white"
            bg="button.500"
            _hover={{ opacity: 0.9 }}
          >
            Top up
          </Button>
        </Flex>
        <Flex
          mt="39px"
          border="2px solid rgba(255, 255,255,0.2)"
          borderRadius="20px"
          direction="column"
          p="30px"
          w={{ base: "350px", md: "500px" }}
        >
          <Text fontWeight="bold" fontSize="24px">
            Transactions
          </Text>
          <Box mt="15px" />
          <SimpleTable
            isLoading={isTransactionsLoading}
            templateColumns="70px 70px 110px 1fr"
            data={transactionsResponse?.credit_transactions}
            columns={transactionsColumns}
          />
          <Flex mt="10px">
            <Button
              _hover={{ opacity: 0.9 }}
              bg="button.500"
              isDisabled={transactionsPage <= 1}
              onClick={() => setTransactionsPage(transactionsPage - 1)}
            >
              <ArrowBackIcon color="white" />
            </Button>
            <Button
              ml="10px"
              _hover={{ opacity: 0.9 }}
              isDisabled={transactionsPage * 4 >= transactionsResponse?.total_credit_transactions}
              bg="button.500"
              onClick={() => setTransactionsPage(transactionsPage + 1)}
            >
              <ArrowForwardIcon color="white" />
            </Button>
          </Flex>
        </Flex>
        <Flex
          mt="25px"
          border="2px solid rgba(255, 255,255,0.2)"
          borderRadius="20px"
          direction="column"
          p="30px"
          w={{ base: "350px", md: "500px" }}
        >
          <Text fontWeight="bold" fontSize="24px">
            Rewards
          </Text>
          <VStack align="start" mt="20px">
            <Flex align="center">
              <Text fontWeight={600}>Opt in to our newsletter and get 20 free credits </Text>
            </Flex>
            {profile?.newsletter_consent && (
              <Text color="gray" fontSize="10px">
                You already opted in!
              </Text>
            )}
          </VStack>

          <Button
            isDisabled={profile?.newsletter_consent}
            color="white"
            bg="button.500"
            minH="40px"
            w="150px"
            mt="25px"
            onClick={handleConfirmClick}
            _hover={{ opacity: 0.9 }}
          >
            Opt in
          </Button>
        </Flex>
        {/* <Flex
          mt="39px"
          border="2px solid rgba(255, 255,255,0.2)"
          borderRadius="20px"
          direction="column"
          p="30px"
          w={{ base: "350px", md: "500px" }}
        >
          <Text fontWeight="bold" fontSize="24px">
            Preferences
          </Text>
          <VStack align="start" mt="15px">
            <Flex align="center">
              <Switch isChecked={nsfwConsent} onChange={() => setNsfwConsent(!nsfwConsent)} />
              <Text ml="15px" fontWeight={600}>
                See NSFW content
              </Text>
            </Flex>
          </VStack>

          <Button
            color="white"
            bg="button.500"
            minH="40px"
            w="150px"
            mt="20px"
            onClick={handleConfirmClick}
            _hover={{ opacity: 0.9 }}
          >
            Confirm
          </Button>
        </Flex> */}
        {!isAPIKeyResponseLoading && (
          <Flex
            mt="39px"
            border="2px solid rgba(255, 255,255,0.2)"
            borderRadius="20px"
            direction="column"
            p="30px"
            w={{ base: "350px", md: "500px" }}
          >
            <Text fontWeight="bold" fontSize="24px">
              API Key
            </Text>
            {APIKeyResponse?.api_key ? (
              <Flex
                direction="row"
                mt="15px"
                position="relative"
                alignItems="center"
                onClick={onAPIKeyCopy}
              >
                <Input
                  value={hasAPIKeyCopied ? "Copied" : APIKey}
                  bg={hasAPIKeyCopied ? "green.800" : "gray.800"}
                  fontSize="14px"
                  color="white"
                  border="unset"
                  cursor="pointer"
                  isReadOnly
                  zIndex={1}
                />
                <Icon
                  position="absolute"
                  right={2}
                  as={FaRegCopy}
                  boxSize={4}
                  bg="transparent"
                  color="white"
                  cursor="pointer"
                  zIndex={2}
                />
              </Flex>
            ) : (
              <Input
                mt="15px"
                value="API Key not set"
                bg="gray.800"
                fontSize="14px"
                color="white"
                border="unset"
                isReadOnly
              />
            )}
            <Flex direction="row" alignItems="center" mt="20px" w="100%">
              <Button
                color="white"
                bg="button.500"
                minH="40px"
                w="150px"
                _hover={{ bg: "button.500", opacity: 0.9 }}
                onClick={handleGenerateAPIKey}
              >
                {APIKeyResponse?.api_key ? "Regenerate" : "Generate"}
              </Button>
              {APIKeyResponse?.api_key && (
                <Button
                  color="white"
                  bg="button.500"
                  minH="40px"
                  w="150px"
                  ml="auto"
                  _hover={{ bg: "button.500", opacity: 0.9 }}
                  onClick={handleDeleteAPIKey}
                >
                  Delete
                </Button>
              )}
            </Flex>
          </Flex>
        )}

        <Flex
          mt="39px"
          border="2px solid rgba(255, 255,255,0.2)"
          borderRadius="20px"
          direction="column"
          p="30px"
          w={{ base: "350px", md: "500px" }}
        >
          <Text fontWeight="bold" fontSize="24px">
            Support
          </Text>
          <Text mt="15px" w={{ base: "315px", md: "100%" }}>
            We usually reply within 1-2 business days but we always hang out on discord{" "}
          </Text>
          <Flex direction={{ base: "column", md: "row" }} w="100% ">
            <Button
              mt="25px"
              w="100%"
              _hover={{ opacity: 0.9, bg: "gray.600" }}
              bg="transparent"
              color="white"
              border="1px solid white"
            >
              <a href="mailto:info@deepmode.com">Email Support</a>
            </Button>
            <Button
              mt="25px"
              w="100%"
              _hover={{ opacity: 0.9, bg: "gray.600" }}
              ml={{ base: "", md: "30px" }}
              bg="transparent"
              color="white"
              border="1px solid white"
            >
              <a target="_blank" href="https://discord.gg/CMwtccKthy">
                Discord Support
              </a>{" "}
            </Button>
          </Flex>
        </Flex>
        <Button
          bg="transparent"
          color="white"
          _hover={{ bg: "red" }}
          mt="25px"
          w="250px"
          onClick={handleDeleteAccount}
        >
          <Text fontWeight={700}>Delete Account</Text>{" "}
        </Button>

        <Box h="150px" />
      </Flex>
    </Flex>
  );
};

export default ProfilePage;
